import React, { useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, X, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";

// Returns {from, to} as yyyy-mm-dd for a preset; '' means open-ended.
export function presetRange(preset) {
  const now = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  if (preset === "all") return { from: "", to: "" };
  if (preset === "30d") { const s = new Date(now); s.setDate(s.getDate() - 30); return { from: iso(s), to: "" }; }
  if (preset === "month") return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: "" };
  if (preset === "today") return { from: iso(now), to: iso(now) };
  return { from: "", to: "" };
}

// Convert a yyyy-mm-dd pair into inclusive ISO datetime bounds for the API.
export function toApiRange({ from, to }) {
  return {
    date_from: from ? `${from}T00:00:00` : undefined,
    date_to: to ? `${to}T23:59:59` : undefined,
  };
}

export function DateRange({ preset, setPreset, range, setRange, testid = "date-range" }) {
  const presets = [["month", "This month"], ["30d", "Last 30 days"], ["today", "Today"], ["all", "All time"]];
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
      <div className="flex rounded-lg border overflow-hidden text-xs">
        {presets.map(([k, label]) => (
          <button key={k} data-testid={`range-${k}`}
            className={`px-2.5 py-1.5 font-medium transition-colors ${preset === k ? "bg-brand-blue text-white" : "bg-white dark:bg-slate-800 hover:bg-slate-50"}`}
            onClick={() => { setPreset(k); setRange(presetRange(k)); }}>{label}</button>
        ))}
      </div>
      <Input type="date" className="w-[140px] h-9" value={range.from} data-testid="range-from"
        onChange={(e) => { setPreset("custom"); setRange((r) => ({ ...r, from: e.target.value })); }} />
      <span className="text-slate-400 text-xs">→</span>
      <Input type="date" className="w-[140px] h-9" value={range.to} data-testid="range-to"
        onChange={(e) => { setPreset("custom"); setRange((r) => ({ ...r, to: e.target.value })); }} />
    </div>
  );
}

export function FilterSelect({ value, onChange, placeholder, options, testid, width = "w-[150px]" }) {
  return (
    <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
      <SelectTrigger className={`${width} h-9`} data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{placeholder}</SelectItem>
        {options.map((o) => (typeof o === "string"
          ? <SelectItem key={o} value={o}>{o}</SelectItem>
          : <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
      </SelectContent>
    </Select>
  );
}

export function FilterShell({ children, count, onClear, exportPath, exportParams, canExport, testid = "filter-bar" }) {
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState(false);
  const doExport = async () => {
    setBusy(true);
    try {
      const res = await api.get(exportPath, { params: exportParams, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "launchx_" + exportPath.split("/").pop() + ".csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) { toast.error("Export failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mb-4 rounded-xl border bg-white dark:bg-slate-800 p-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <button className="flex items-center gap-1.5 text-sm font-semibold text-slate-600 dark:text-slate-300" onClick={() => setOpen((o) => !o)} data-testid="filter-toggle">
          <SlidersHorizontal className="w-4 h-4" /> Filters {typeof count === "number" && <span className="text-brand-blue">· {count} result{count === 1 ? "" : "s"}</span>}
        </button>
        <div className="flex items-center gap-2">
          {onClear && <Button size="sm" variant="ghost" className="h-8 gap-1 text-slate-500" onClick={onClear} data-testid="filter-clear"><X className="w-3.5 h-3.5" /> Clear</Button>}
          {canExport && exportPath && <Button size="sm" variant="outline" className="h-8 gap-1" disabled={busy} onClick={doExport} data-testid="filter-export"><Download className="w-3.5 h-3.5" /> Export CSV</Button>}
        </div>
      </div>
      {open && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}
